import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

/**
 * NProgress-style top bar that animates on every route change.
 *
 * React Router 5 doesn't expose a "route done loading" event — components
 * mount and might suspend on async data, but the router itself doesn't
 * know about it. So this is a *progress hint*, not a real loading bar:
 * on every location change we drive 0 → 90 over ~600ms, sit at 90, then
 * jump to 100 + fade out on the NEXT navigation (or after a quiet timeout
 * if no further nav happens within 800ms). This matches what NProgress
 * itself does in single-page apps.
 *
 * Visual: a 2px gradient bar at the very top of the viewport, accent
 * colour with a soft glow. Renders nothing when idle (no DOM cost). Honours
 * prefers-reduced-motion — the bar still appears but without the smooth
 * width animation (an opacity blink instead).
 */
export default function TopProgressBar() {
  const history = useHistory();
  const [active, setActive] = useState(false);
  const [done, setDone] = useState(false);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef<number | null>(null);
  const idleTimerRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);

  useEffect(() => {
    if (!history) return;

    const start = () => {
      // Cancel any pending finish — restarting mid-flight is normal in
      // fast navigation (user clicks link B while link A is still
      // animating). Drop the old bar and start a fresh sweep.
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);

      setDone(false);
      setActive(true);
      setProgress(0);
      startTimeRef.current = performance.now();

      const animate = (now: number) => {
        const elapsed = now - startTimeRef.current;
        // Ease-out: snap quickly to 60%, then crawl to 90%. Never hit
        // 100 — that happens via finish() on the next navigation or via
        // the idle timer below.
        const t = Math.min(1, elapsed / 700);
        const eased = 1 - Math.pow(1 - t, 3);
        const next = Math.min(90, Math.round(eased * 90));
        setProgress(next);
        if (t < 1) rafRef.current = requestAnimationFrame(animate);
      };
      rafRef.current = requestAnimationFrame(animate);

      // Quiet-period finish — if nothing else navigates within 800ms,
      // assume the route settled and complete the bar.
      idleTimerRef.current = window.setTimeout(finish, 800);
    };

    const finish = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      setProgress(100);
      setDone(true);
      // After the fade-out transition completes, unmount the bar so the
      // next nav starts fresh from 0.
      window.setTimeout(() => {
        setActive(false);
        setProgress(0);
        setDone(false);
      }, 250);
    };

    // history.listen returns an unsubscribe fn. Fires on push/pop/replace.
    const unlisten = history.listen(() => {
      // If a bar is already showing, complete it before starting a new one
      // so quick double-clicks don't lose the visual handshake.
      if (active) finish();
      start();
    });

    return () => {
      unlisten();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
    };
    // `active` is intentionally excluded — re-binding the listener on every
    // bar state change would create cascading listeners.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  if (!active) return null;

  return (
    <div className={`route-progress ${done ? 'is-done' : ''}`} role="progressbar" aria-label="Loading route" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
      <div className="route-progress-bar" style={{ ['--route-progress' as any]: `${progress}%` }} />
    </div>
  );
}
